import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bank, Buildings, Check, CheckCircle, Clock, Coins, EnvelopeSimple, FileText, Globe, Hash, IdentificationCard, Lightning, LinkSimple, MapPin,
  MapTrifold, PauseCircle, Percent, Phone, Receipt, Siren, Signpost, Star, Trash, User, UserPlus, Users, Warning, X,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, PanelZeile, SchalterZeile, SegmentedControl, Stepper, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { eur } from '../../../../../lib/immobilien/format';
import type { KontaktKanal } from '../../../../../lib/kontakt/kontaktKanaele';
import {
  bankAus, bankFehler, bankPayload, geaendert, KONTAKT_ROLLEN, kondAus, kondFehler, kondPayload, kontaktFehler, loeschText, rollenLabel, stammAus, stammFehler,
  stammPayload, type BankEntwurf, type KondEntwurf, type KontaktRolle, type StammEntwurf, type Verknuepfung,
} from '../../../../../lib/dienstleister/stammdaten';
import {
  aktualisiereDl, ladeDlKanaele, legeKontaktAn, loescheDl, loescheKontakt, pruefeDlLoeschen, speichereDlStammdaten,
} from '../../../../../lib/dienstleister/aktionen';
import { meldeDlGeaendert } from '../../../../../lib/dienstleister/useDienstleisterDetail';
import { ZustellwegeAuswahl } from '../../../../../components/schreiben/ZustellwegeAuswahl';
import { useDlSeite } from '../DienstleisterSeitenKontext';
import { Aktionslink, BEREICH_STIL, fehlerText, GLAS, telHref, useVerlassen, Verwerfen } from './teile';
import { WeitereKontaktwege } from '../../../../../components/kontakt/WeitereKontaktwege';
import { kanaeleGleich } from '../../../../../lib/kontakt/kontaktKanaele';

// ── Stammdaten & Kontakt (Screens 25, 26) ──────────────────────────────────────
export function StammdatenPanel() {
  const s = useDlSeite();
  const navigate = useNavigate();
  const d = s.daten.dienstleister;
  const [start, setStart] = useState<StammEntwurf>(() => stammAus(d));
  const [e, setE] = useState<StammEntwurf>(start);
  const [kanaele, setKanaele] = useState<KontaktKanal[] | null>(null);
  const [kanalFehler, setKanalFehler] = useState<string | null>(null);
  const [kanaeleStart, setKanaeleStart] = useState<KontaktKanal[] | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [pruefung, setPruefung] = useState<Verknuepfung[] | 'laedt' | null>(null);
  const [loescht, setLoescht] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof StammEntwurf>(k: K) => (v: StammEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));
  const fehler = stammFehler(e);
  // #520: auch weitere E-Mails/Telefonnummern zählen als Änderung
  const anders = geaendert(e, start) || !kanaeleGleich(kanaele, kanaeleStart);
  const [verwerfen, setVerwerfen] = useVerlassen(anders);
  const aktiv = d.aktiv !== false;

  useEffect(() => {
    let lebt = true;
    ladeDlKanaele(d).then(k => { if (lebt) { setKanaele(k); setKanaeleStart(k); } }).catch((err: unknown) => { if (lebt) setKanalFehler(fehlerText(err)); });
    return () => { lebt = false; };
  }, [d]);

  const speichere = async () => {
    if (fehler.name) { zeige('Name ist Pflicht', true); return; }
    if (Object.keys(fehler).length) { zeige('Bitte die markierten Felder prüfen', true); return; }
    if (!kanaele) { zeige(`E-Mail/Telefon nicht geladen${kanalFehler ? `: ${kanalFehler}` : ''}`, true); return; }
    setLaeuft(true);
    try {
      const gespeichert = await speichereDlStammdaten(d.id, stammPayload(e), kanaele, e.email, e.telefon);
      setKanaele(gespeichert);
      setKanaeleStart(gespeichert);
      setStart(e);
      zeige('Stammdaten gespeichert');
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  const setzeStatus = async (an: boolean) => {
    setLaeuft(true);
    try {
      await aktualisiereDl(d.id, { aktiv: an });
      setPruefung(null);
      zeige(an ? `${s.name} wieder aktiv` : `${s.name} auf inaktiv gesetzt – wird nicht mehr vorgeschlagen`);
      meldeDlGeaendert(d.id);
    } catch (err) { zeige(`Nicht geändert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  const starteLoeschen = async () => {
    setPruefung('laedt');
    try { setPruefung(await pruefeDlLoeschen(d.id, s.heute)); } catch (err) { setPruefung(null); zeige(`Verknüpfungen konnten nicht geprüft werden: ${fehlerText(err)}`, true); }
  };
  const loesche = async () => {
    setLoescht(true);
    try {
      await loescheDl(d.id, s.heute);
      s.registriereVerlassen(null);
      meldeDlGeaendert(d.id);
      navigate('/admin/dienstleister', { replace: true });
    } catch (err) { setLoescht(false); setPruefung(null); zeige(`Nicht gelöscht: ${fehlerText(err)}`, true); }
  };
  const ICON: Record<Verknuepfung['art'], typeof Buildings> = { objekt: Buildings, ticket: LinkSimple, rechnung: Receipt };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Stammdaten</KartenTitel>
        <FeldRaster minBreite={180}>
          <TextFeld label="Name *" icon={Buildings} wert={e.name} onChange={setze('name')} fehler={fehler.name} voll />
          <TextFeld label="Ansprechpartner" icon={User} wert={e.ansprechpartner} onChange={setze('ansprechpartner')} />
          <TextFeld label="E-Mail" icon={EnvelopeSimple} type="email" wert={e.email} onChange={setze('email')} fehler={fehler.email} autoComplete="off" />
          <TextFeld label="Telefon" icon={Phone} type="tel" wert={e.telefon} onChange={setze('telefon')} autoComplete="off" />
          <TextFeld label="Notdienst-Telefon" icon={Siren} type="tel" wert={e.notdienstTelefon} onChange={setze('notdienstTelefon')} placeholder="optional" autoComplete="off" />
          <TextFeld label="Website" icon={Globe} wert={e.website} onChange={setze('website')} />
          <TextFeld label="Straße" icon={Signpost} wert={e.strasse} onChange={setze('strasse')} />
          <TextFeld label="Nr." icon={Hash} wert={e.hausnummer} onChange={setze('hausnummer')} />
          <TextFeld label="PLZ" icon={MapPin} inputMode="numeric" wert={e.plz} onChange={setze('plz')} fehler={fehler.plz} />
          <TextFeld label="Stadt" icon={MapTrifold} wert={e.stadt} onChange={setze('stadt')} />
        </FeldRaster>
        {kanaele && <WeitereKontaktwege kanaele={kanaele} onChange={setKanaele} disabled={laeuft} />}
        <ZustellwegeAuswahl art="dienstleister" id={d.id} />
        <textarea value={e.notizen} onChange={ev => setze('notizen')(ev.target.value)} rows={3} aria-label="Notizen" placeholder="Notizen: Zugang, Schlüssel, Besonderheiten …" style={BEREICH_STIL} />
        <SchalterZeile label={aktiv ? 'Status: Aktiv' : 'Status: Inaktiv'} icon={aktiv ? CheckCircle : PauseCircle} an={aktiv} disabled={laeuft}
          onChange={an => { void setzeStatus(an); }} zusatz={aktiv ? undefined : 'Wird bei Tickets nicht mehr vorgeschlagen'} />
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || !anders} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</ImmoButton>
        </div>
      </InnenKarte>

      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {e.telefon.trim() ? <Aktionslink href={telHref(e.telefon)} icon={Phone}>Anrufen</Aktionslink>
          : <ImmoButton groesse={44} icon={Phone} disabled title="Keine Telefonnummer hinterlegt">Anrufen</ImmoButton>}
        {e.email.trim() ? <Aktionslink href={`mailto:${e.email.trim()}`} icon={EnvelopeSimple}>E-Mail</Aktionslink>
          : <ImmoButton groesse={44} icon={EnvelopeSimple} disabled title="Keine E-Mail hinterlegt">E-Mail</ImmoButton>}
        <ImmoButton groesse={44} icon={Trash} disabled={pruefung === 'laedt'} onClick={() => { void starteLoeschen(); }}>
          {pruefung === 'laedt' ? 'Verknüpfungen werden geprüft …' : 'Dienstleister löschen'}
        </ImmoButton>
      </div>

      {Array.isArray(pruefung) && pruefung.length > 0 && (
        <>
          <InnenKarte style={GLAS} gap={4}>
            <KartenTitel>Löschen nicht möglich</KartenTitel>
            <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>{loeschText(s.name, pruefung.length)}</span>
          </InnenKarte>
          <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
            <div style={{ padding: '0 6px 6px' }}><KartenTitel>Verknüpfungen</KartenTitel></div>
            {pruefung.map((v, i) => <PanelZeile key={i} icon={ICON[v.art]} ton="bad" titel={v.titel} sub={v.detail} />)}
          </InnenKarte>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton art="akzent" groesse={44} icon={Check} onClick={() => setPruefung(null)}>Verstanden</ImmoButton>
            {aktiv && <ImmoButton groesse={44} icon={PauseCircle} disabled={laeuft} onClick={() => { void setzeStatus(false); }}>Stattdessen inaktiv setzen</ImmoButton>}
          </div>
        </>
      )}
      {Array.isArray(pruefung) && pruefung.length === 0 && (
        <BestaetigenDialog titel="Dienstleister löschen?"
          text={`Möchten Sie ${s.name} endgültig löschen? Kategorien und Regel-Zuordnungen werden entfernt, ebenso Kontaktwege, Notizen und Ansprechpartner. Bezahlte Rechnungen, Tickets, Termine und Dokumente bleiben ohne Zuordnung erhalten.`}
          bestaetigenLabel="Endgültig löschen" icon={Trash} laeuft={loescht} onBestaetigen={() => { void loesche(); }} onAbbrechen={() => setPruefung(null)} />
      )}
      {verwerfen && <Verwerfen text="Die geänderten Stammdaten gehen verloren." onAbbrechen={() => setVerwerfen(false)} />}
    </>
  );
}

// ── Ansprechpartner (Screen 27) ────────────────────────────────────────────────
const LEER_KONTAKT = { name: '', telefon: '', email: '' };

export function AnsprechpartnerPanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const [rolle, setRolle] = useState<KontaktRolle>('monteur');
  const [k, setK] = useState(LEER_KONTAKT);
  const [laeuft, setLaeuft] = useState(false);
  const [entfernen, setEntfernen] = useState<{ id: string; name: string } | null>(null);
  const [toast, zeige] = useToastPill();
  const fehler = k.name || k.email ? kontaktFehler(k) : null;
  const ausfuehren = async (fn: () => Promise<void>, text: string) => {
    setLaeuft(true);
    try { await fn(); zeige(text); meldeDlGeaendert(d.id); } catch (err) { zeige(fehlerText(err), true); } finally { setLaeuft(false); }
  };
  const icon = (r: string) => (r === 'bereitschaft' ? Siren : r === 'rechnungen' ? Receipt : User);
  const ton = (r: string): 'bad' | 'info' | 'acc' => (r === 'bereitschaft' ? 'bad' : r === 'rechnungen' ? 'info' : 'acc');
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {entfernen && (
        <BestaetigenDialog titel={`${entfernen.name} entfernen?`} text="Der Ansprechpartner wird aus der Liste entfernt." bestaetigenLabel="Entfernen" laeuft={laeuft}
          onAbbrechen={() => setEntfernen(null)} onBestaetigen={() => { const x = entfernen; void ausfuehren(() => loescheKontakt(x.id), `${x.name} entfernt`).then(() => setEntfernen(null)); }} />
      )}
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>{`${s.daten.kontakte.length} Ansprechpartner`}</KartenTitel></div>
        {s.daten.kontakte.length === 0 && (
          <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>
            Noch keine Ansprechpartner hinterlegt{s.daten.zeile.ansprechpartner ? ` – in den Stammdaten steht „${s.daten.zeile.ansprechpartner}“` : ''}.
          </span>
        )}
        {s.daten.kontakte.map(x => (
          <div key={x.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
            <div className="flex-1 min-w-0">
              {x.telefon ? (
                <a href={telHref(x.telefon)} aria-label={`${x.name} anrufen`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <PanelZeile icon={icon(x.rolle)} ton={ton(x.rolle)} titel={x.name} sub={[rollenLabel(x.rolle), x.telefon, x.email ?? ''].filter(Boolean).join(' · ')} chip={{ text: rollenLabel(x.rolle), ton: ton(x.rolle) }} />
                </a>
              ) : (
                <PanelZeile icon={icon(x.rolle)} ton={ton(x.rolle)} titel={x.name} sub={[rollenLabel(x.rolle), x.email ?? 'keine Telefonnummer'].filter(Boolean).join(' · ')} chip={{ text: rollenLabel(x.rolle), ton: ton(x.rolle) }} />
              )}
            </div>
            <ImmoButton groesse={32} icon={X} aria-label={`${x.name} entfernen`} title="Entfernen" onClick={() => setEntfernen({ id: x.id, name: x.name })} />
          </div>
        ))}
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Ansprechpartner hinzufügen</KartenTitel>
        <SegmentedControl<KontaktRolle> ariaLabel="Rolle" hoehe={34} className="self-start" wert={rolle} onChange={setRolle} segmente={KONTAKT_ROLLEN.map(r => ({ key: r.key, label: r.label }))} />
        <FeldRaster minBreite={180}>
          <TextFeld label="Name" icon={User} placeholder="Vor- und Nachname" wert={k.name} onChange={v => setK(x => ({ ...x, name: v }))} />
          <TextFeld label="Telefon" icon={Phone} type="tel" wert={k.telefon} onChange={v => setK(x => ({ ...x, telefon: v }))} />
          <TextFeld label="E-Mail" icon={EnvelopeSimple} type="email" wert={k.email} onChange={v => setK(x => ({ ...x, email: v }))} />
        </FeldRaster>
        {fehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>{fehler}</span>}
        <ImmoButton art="akzent" icon={UserPlus} className="self-start" disabled={laeuft || !k.name.trim() || !!fehler}
          onClick={() => { const name = k.name.trim(); void ausfuehren(async () => { await legeKontaktAn(d.id, { ...k, rolle }); setK(LEER_KONTAKT); }, `${name} hinzugefügt`); }}>
          Hinzufügen
        </ImmoButton>
      </InnenKarte>
    </>
  );
}

// ── Konditionen & Beauftragung (Screen 17) ─────────────────────────────────────
export function KonditionenPanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const [start, setStart] = useState<KondEntwurf>(() => kondAus(d));
  const [e, setE] = useState<KondEntwurf>(start);
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof KondEntwurf>(k: K) => (v: KondEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));
  const fehler = kondFehler(e);
  const anders = geaendert(e, start);
  const [verwerfen, setVerwerfen] = useVerlassen(anders);
  const speichere = async () => {
    if (Object.keys(fehler).length) { zeige('Bitte die markierten Felder prüfen', true); return; }
    setLaeuft(true);
    try { await aktualisiereDl(d.id, kondPayload(e)); setStart(e); zeige('Konditionen gespeichert'); meldeDlGeaendert(d.id); }
    catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Preise</KartenTitel>
        <FeldRaster minBreite={180}>
          <TextFeld label="Stundensatz" icon={Coins} inputMode="decimal" suffix="€ / h" wert={e.stundensatz} onChange={setze('stundensatz')} fehler={fehler.stundensatz} />
          <TextFeld label="Anfahrtspauschale" icon={Coins} inputMode="decimal" suffix="€" wert={e.anfahrt} onChange={setze('anfahrt')} fehler={fehler.anfahrt} />
          <TextFeld label="Notdienstzuschlag" icon={Percent} inputMode="decimal" suffix="%" wert={e.notdienstzuschlag} onChange={setze('notdienstzuschlag')} fehler={fehler.notdienstzuschlag} />
          <TextFeld label="Zahlungsziel" icon={Clock} inputMode="numeric" suffix="Tage" wert={e.zahlungsziel} onChange={setze('zahlungsziel')} fehler={fehler.zahlungsziel} />
          <TextFeld label="Skonto" icon={Percent} inputMode="decimal" suffix="%" wert={e.skontoProzent} onChange={setze('skontoProzent')} fehler={fehler.skontoProzent} />
          <TextFeld label="Skonto innerhalb" icon={Clock} inputMode="numeric" suffix="Tage" wert={e.skontoTage} onChange={setze('skontoTage')} fehler={fehler.skontoTage} />
        </FeldRaster>
        <div className="flex flex-wrap items-center" style={{ gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Freigabegrenze</span>
          <Stepper label="Freigabegrenze" wert={e.freigabegrenze} onChange={setze('freigabegrenze')} min={0} max={10000} schritt={100} einheit="€" />
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>bis {eur(e.freigabegrenze, { nachkomma: 0 })} ohne Rückfrage, darüber Freigabe durch Eigentümer bzw. WEG-Beschluss</span>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Beauftragung</KartenTitel>
        <SegmentedControl<'direkt' | 'angebot'> ariaLabel="Beauftragung" hoehe={34} className="self-start" wert={e.modus} onChange={setze('modus')}
          segmente={[{ key: 'direkt', label: 'Direkt beauftragen', icon: Lightning }, { key: 'angebot', label: 'Erst Angebot', icon: FileText }]} />
        <div className="flex flex-col" style={{ gap: 6 }}>
          <SchalterZeile an={e.notfall} onChange={setze('notfall')} icon={Siren} label="Notfallkontakt (24/7)" hintergrundAn="var(--bad-soft)" />
          <SchalterZeile an={e.bevorzugt} onChange={setze('bevorzugt')} icon={Star} label="Bevorzugt bei Tickets" />
          {e.bevorzugt && <SchalterZeile an={e.vertretung} onChange={setze('vertretung')} icon={Users} label="Nur als Vertretung" klein zusatz="Wird erst nach der ersten Wahl des Gewerks vorgeschlagen" />}
        </div>
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || !anders} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Konditionen speichern'}</ImmoButton>
        </div>
      </InnenKarte>
      {verwerfen && <Verwerfen text="Die geänderten Konditionen gehen verloren." onAbbrechen={() => setVerwerfen(false)} />}
    </>
  );
}

// ── Bank & Steuer (Screen 18) ──────────────────────────────────────────────────
export function BankPanel() {
  const s = useDlSeite();
  const d = s.daten.dienstleister;
  const [start, setStart] = useState<BankEntwurf>(() => bankAus(d));
  const [e, setE] = useState<BankEntwurf>(start);
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof BankEntwurf>(k: K) => (v: BankEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));
  const fehler = bankFehler(e);
  const anders = geaendert(e, start);
  const [verwerfen, setVerwerfen] = useVerlassen(anders);
  const speichere = async () => {
    if (Object.keys(fehler).length) { zeige('Bitte die markierten Felder prüfen', true); return; }
    setLaeuft(true);
    try { await aktualisiereDl(d.id, bankPayload(e)); setStart(e); zeige('Bank- und Steuerdaten gespeichert'); meldeDlGeaendert(d.id); }
    catch (err) { zeige(`Nicht gespeichert: ${fehlerText(err)}`, true); } finally { setLaeuft(false); }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Bankverbindung</KartenTitel>
        <FeldRaster minBreite={200}>
          <TextFeld label="IBAN" icon={Bank} wert={e.iban} onChange={setze('iban')} placeholder="DE00 …" fehler={fehler.iban} autoComplete="off" />
          <TextFeld label="Kontoinhaber" icon={User} wert={e.kontoinhaber} onChange={setze('kontoinhaber')} placeholder={s.name} />
          <TextFeld label="BIC" icon={Hash} wert={e.bic} onChange={setze('bic')} fehler={fehler.bic} autoComplete="off" />
        </FeldRaster>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Steuer</KartenTitel>
        <FeldRaster minBreite={200}>
          <TextFeld label="USt-IdNr." icon={IdentificationCard} wert={e.ustId} onChange={setze('ustId')} placeholder="DE123456789" fehler={fehler.ustId} />
          <TextFeld label="Steuer-Nr." icon={Hash} wert={e.steuerNr} onChange={setze('steuerNr')} />
        </FeldRaster>
        <div className="flex flex-col" style={{ gap: 6 }}>
          <SchalterZeile an={e.kleinunternehmer} onChange={setze('kleinunternehmer')} icon={Receipt} label="Kleinunternehmer (§ 19 UStG)" />
          <SchalterZeile an={e.reverseCharge} onChange={setze('reverseCharge')} icon={Warning} label="§ 13b Reverse Charge (Bauleistungen)" />
        </div>
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || !anders} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</ImmoButton>
        </div>
      </InnenKarte>
      {verwerfen && <Verwerfen text="Die geänderten Bank- und Steuerdaten gehen verloren." onAbbrechen={() => setVerwerfen(false)} />}
    </>
  );
}

